import { useWidgetData } from "../Contexts/dashboardHooks";
import { GROUPBY_OPTIONS } from "../AddWidgetModal/constants";
import { resolveDateRange } from "../Utils/resolveDateRange";
import { formatSeriesLabel } from "../Processing/seriesLabel";
import { WidgetInfoPopoverContent, type WidgetInfoRow } from "../WidgetComponents/WidgetInfoPopover";
import type { ChartComponentSpec, ChartSeriesDef } from "../Processing/types";
import { resolveSeriesMisc } from "../Processing/seriesMisc";

const MAX_SUBJECT_ITEMS = 20;

/** Which series' date fields this series actually renders — its own, or the first's. */
function resolveDate(s: ChartSeriesDef, spec: ChartComponentSpec): string {
  const src = resolveSeriesMisc(s, spec.series).date;
  // The label itself comes from the shared resolver. This used to be a second copy of
  // it, which knew neither the training-count presets nor the latest_n window, so it
  // printed the raw preset value and mislabelled an Advanced window as a day range.
  return resolveDateRange(src);
}

function resolveSubjects(s: ChartSeriesDef, spec: ChartComponentSpec): string {
  const src = resolveSeriesMisc(s, spec.series).subject;
  const names = src?.selectedSubjectNames ?? [];
  if (names.length === 0) return "Full Team";
  if (names.length <= MAX_SUBJECT_ITEMS) return names.join(", ");
  return `${names.slice(0, MAX_SUBJECT_ITEMS).join(", ")} (+${names.length - MAX_SUBJECT_ITEMS} more)`;
}

export function ChartInfo({ id }: { id: string }) {
  const data = useWidgetData(id);
  if (!data || data.type !== "chart") return null;
  const spec = data.spec;
  if (!spec.series.length) return null;

  const groupByLabel = GROUPBY_OPTIONS.find((o) => o.value === spec.chart_builder_misc?.groupBy)?.label
    ?? spec.chart_builder_misc?.groupBy ?? "Day";
  const groupBy = spec.widgetMutations?.showAllDatesInRange
    ? `${groupByLabel} (all dates in range)`
    : groupByLabel;

  // Filter out ACWR companion series that just mirror the parent
  const acwrParentIds = new Set(
    spec.series
      .filter((s) => s.series_builder_misc?.isAcwrCompanion)
      .map((s) => s.series_builder_misc.parentQueryId)
      .filter(Boolean),
  );
  const visibleSeries = spec.series.filter((s) => !acwrParentIds.has(s.queryId));

  const rows: WidgetInfoRow[] = visibleSeries.map((s) => {
    const labelMisc = resolveSeriesMisc(s, spec.series).subject;
    const label = s.displayNameOverride?.trim()
      ? s.displayNameOverride
      : formatSeriesLabel(s.displayName, s.metricAgg, s.metricIdentifierType, labelMisc);

    const extras: string[] = [];
    if (s.plotMutations.showDataLabels) extras.push("Show Labels");
    if (s.plotMutations.showTrendline) extras.push("Trendline");
    if (s.plotMutations.showACWRRefLines) extras.push("ACWR Ref Lines");
    if (s.plotMutations.lineSmoothing) extras.push("Smoothed");
    if (s.chartType === "line" && s.plotMutations.areaFill) extras.push("Area");
    if (s.plotMutations.showReferenceBands && (s.referenceBands?.length ?? 0) > 0) extras.push("Reference Bands");

    return {
      queryId: s.queryId,
      label,
      aggLabel: { avg: "Average", sum: "Sum", best: "Best" }[s.metricAgg] ?? s.metricAgg,
      dates: resolveDate(s, spec),
      subjects: resolveSubjects(s, spec),
      groupBy,
      extras,
    };
  });

  return <WidgetInfoPopoverContent rows={rows} />;
}
